import type { EmbedEvent } from "./api";
import { eventSpansMultipleDays, formattersFor, safeTz } from "./date";

export type WeekStart = "sun" | "mon";

export type CalendarDay = {
  date: Date;
  dayKey: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  singleDayEvents: EmbedEvent[];
};

export type CalendarSpan = {
  event: EmbedEvent;
  startCol: number;
  endCol: number;
  row: number;
  isStart: boolean;
  isEnd: boolean;
};

export type CalendarWeek = {
  days: CalendarDay[];
  spans: CalendarSpan[];
  spanRowCount: number;
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function startOfGrid(monthDate: Date, weekStart: WeekStart): Date {
  const first = startOfMonth(monthDate);
  const dow = first.getDay(); // 0=Sun
  const offset = weekStart === "sun" ? dow : (dow + 6) % 7;
  const start = new Date(first);
  start.setDate(start.getDate() - offset);
  return start;
}

export function endOfGrid(monthDate: Date, weekStart: WeekStart): Date {
  const last = endOfMonth(monthDate);
  const dow = last.getDay();
  const offset = weekStart === "sun" ? 6 - dow : (7 - dow) % 7;
  const end = new Date(last);
  end.setDate(end.getDate() + offset);
  return end;
}

export function monthGridDateRange(
  monthDate: Date,
  weekStart: WeekStart,
): { start: Date; end: Date } {
  const start = startOfGrid(monthDate, weekStart);
  const endInclusive = endOfGrid(monthDate, weekStart);
  // Buffer ±1 day to absorb timezone offsets when fetching from the API.
  const bufferedStart = new Date(start);
  bufferedStart.setDate(bufferedStart.getDate() - 1);
  const bufferedEnd = new Date(endInclusive);
  bufferedEnd.setDate(bufferedEnd.getDate() + 2);
  return { start: bufferedStart, end: bufferedEnd };
}

function eventDayKeys(event: EmbedEvent): {
  startKey: string;
  endKey: string;
} {
  const fmt = formattersFor(safeTz(event.timezone)).dayKey;
  return {
    startKey: fmt.format(new Date(event.starts_at)),
    endKey: fmt.format(new Date(event.ends_at)),
  };
}

export function buildMonthGrid(
  monthDate: Date,
  events: EmbedEvent[],
  weekStart: WeekStart = "sun",
): CalendarWeek[] {
  const gridStart = startOfGrid(monthDate, weekStart);
  const gridEnd = endOfGrid(monthDate, weekStart);
  const monthIndex = monthDate.getMonth();
  const todayKey = localDayKey(new Date());

  const allDays: CalendarDay[] = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const date = new Date(cursor);
    const key = localDayKey(date);
    allDays.push({
      date,
      dayKey: key,
      isCurrentMonth: date.getMonth() === monthIndex,
      isToday: key === todayKey,
      singleDayEvents: [],
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  const weeks: CalendarWeek[] = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push({
      days: allDays.slice(i, i + 7),
      spans: [],
      spanRowCount: 0,
    });
  }

  const indexByKey = new Map<string, number>();
  allDays.forEach((d, idx) => indexByKey.set(d.dayKey, idx));

  const firstKey = allDays[0]?.dayKey ?? "";
  const lastKey = allDays[allDays.length - 1]?.dayKey ?? "";

  const sortedEvents = [...events].sort((a, b) => {
    if (a.starts_at < b.starts_at) return -1;
    if (a.starts_at > b.starts_at) return 1;
    return 0;
  });

  for (const event of sortedEvents) {
    const { startKey, endKey } = eventDayKeys(event);

    // Skip events fully outside the grid window.
    if (endKey < firstKey || startKey > lastKey) continue;

    // Clip to grid.
    let startIdx = indexByKey.get(startKey);
    if (startIdx === undefined) {
      startIdx = startKey < firstKey ? 0 : -1;
    }
    let endIdx = indexByKey.get(endKey);
    if (endIdx === undefined) {
      endIdx = endKey > lastKey ? allDays.length - 1 : -1;
    }
    if (startIdx === -1 || endIdx === -1) continue;

    // Multi-day classification uses the event's own timezone so a 6–8 PM
    // Chicago event isn't flagged multi-day when viewed from UTC.
    if (!eventSpansMultipleDays(event)) {
      allDays[startIdx].singleDayEvents.push(event);
      continue;
    }

    // Multi-day event: split across weeks into spans. Each span renders as a
    // ribbon covering startCol..endCol of its week (first visible span shows
    // the date range + title; continuations are blank).
    let cur = startIdx;
    while (cur <= endIdx) {
      const weekIdx = Math.floor(cur / 7);
      const weekStartIdx = weekIdx * 7;
      const weekEndIdx = weekStartIdx + 6;
      const spanStart = Math.max(cur, weekStartIdx);
      const spanEnd = Math.min(endIdx, weekEndIdx);
      const startCol = spanStart - weekStartIdx;
      const endCol = spanEnd - weekStartIdx;

      const week = weeks[weekIdx];

      // Find the lowest free row that doesn't overlap any existing span.
      let row = 0;
      while (
        week.spans.some(
          (s) => s.row === row && !(s.endCol < startCol || s.startCol > endCol),
        )
      ) {
        row++;
      }

      week.spans.push({
        event,
        startCol,
        endCol,
        row,
        isStart: cur === startIdx,
        isEnd: spanEnd === endIdx,
      });

      if (row + 1 > week.spanRowCount) {
        week.spanRowCount = row + 1;
      }

      cur = spanEnd + 1;
    }
  }

  return weeks;
}
